/**
 * Single declarative navigation config — replaces the legacy app's dual
 * menu systems. Filtered by role + ACL + module entitlement at render time
 * (see filter-nav.ts).
 *
 * # Convention: Add buttons on listing pages
 *
 * Sidebar groups DO NOT include "Add X" / "Create X" sub-items. Where a
 * group used to have an Add+List pair the entry collapses to a single
 * leaf pointing at the listing route. The "+ Add X" action lives on the
 * listing page itself via `PageHeader` `actions`.
 *
 * # Module gating
 *
 * `requiresAnyModuleId?: number[]` declares the moduleIds that must be
 * present in the current user's entitlement set for the item to show.
 * If any of the listed IDs is in the set, the item passes. SADMIN /
 * SUB_SUPER_ADMIN bypass module gating entirely (see filter-nav.ts).
 *
 * Module-ID cheat sheet (from legacy):
 *   1  = Custom IVR              (USER)
 *   3  = Voice Webhooks          (USER)
 *   4  = API Management          (USER)
 *   5  = Dedicated CLI           (ADMIN)
 *   6  = Inbound Campaigns       (USER, ADMIN)
 *   7  = TTS (Text-to-Speech)    (USER)
 *   9  = SMS Webhooks            (USER)
 *   10 = TTS Flow / Continuous   (USER)
 *   11, 12 = Report Scheduling   (USER)
 *   16 = API Docs                (USER, ADMIN)
 */
import {
  BookAudio,
  ChartArea,
  Cog,
  FileBarChart,
  FileText,
  Globe,
  Headphones,
  History,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  MapPin,
  Megaphone,
  PhoneCall,
  PhoneOutgoing,
  Server,
  ShieldCheck,
  Users,
  Webhook,
  Wifi,
  Bell,
  ShieldAlert,
  type LucideIcon,
} from 'lucide-react';
import { USER_ROLES, type UserRole } from '@/lib/constants/roles';

export interface NavLeaf {
  label: string;
  to: string;
  icon?: LucideIcon;
  roles: UserRole[];
  /** Dotted path into the ACL tree, e.g. "Manage Users.Create User". */
  permissionPath?: string;
  /**
   * Module IDs that grant access. If any of these is in the current
   * user's module set, the item shows. Ignored for SADMIN /
   * SUB_SUPER_ADMIN, who get module-independent access.
   */
  requiresAnyModuleId?: number[];
  /**
   * ACL module name (e.g. "TTS Plans"). For reseller / end-user, the item
   * is hidden when the nearest sub-superadmin's ACL does not grant
   * view_permission on this module. No effect when they have no such parent.
   */
  inheritedModule?: string;
  /** Nested ACL path (e.g. "Manage CLI.CLI Logs") checked in the nearest sub-superadmin's ACL for reseller/user. */
  inheritedPath?: string;
}

export interface NavGroup {
  label: string;
  icon: LucideIcon;
  roles: UserRole[];
  permissionPath?: string;
  requiresAnyModuleId?: number[];
  inheritedModule?: string;
  children: NavLeaf[];
}

export type NavItem = NavLeaf | NavGroup;

export function isGroup(item: NavItem): item is NavGroup {
  return 'children' in item;
}

const { ADMIN, USER, S_ADMIN, SUB_USER, SUB_SUPER_ADMIN } = USER_ROLES;

export const NAV_ITEMS: NavItem[] = [
  /* ------------- Top of sidebar ----------------------------------- */
  {
    label: 'Overview',
    icon: LayoutDashboard,
    to: '/',
    roles: [ADMIN, USER, S_ADMIN, SUB_SUPER_ADMIN],
  },
  {
    label: 'My Activity',
    icon: FileBarChart,
    to: '/sub-user-report',
    roles: [SUB_USER],
  },
  {
    label: 'Credit Ledger',
    icon: History,
    to: '/credits-history',
    roles: [USER],
  },
  {
    label: 'Health',
    icon: Server,
    to: '/monitoring',
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'Server Monitoring',
  },

  /* ------------- People ------------------------------------------- */

  {
    label: 'Users',
    icon: Users,
    roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'Manage Users',
    children: [
      // Listing — also hosts the "+ Add User" button (→ /create-user).
      { label: 'Directory', to: '/user-list', roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Users.Directory' },
      { label: 'Billing Summary', to: '/reseller-summary', roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Users.Reseller Summary' },
      { label: 'Credit Ledger', to: '/credits-history', roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Users.Credit Ledger' },
      { label: 'Call Records', to: '/call-detail-records', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Users.Call Records' },
      { label: 'Tier Pricing', to: '/user-settings', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Users.Tier Pricing' },
      { label: 'Plan Audit', to: '/voice-plan-logs', roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Users.Voice Plan Logs' },
      { label: 'Activity Logs', to: '/activity-logs', roles: [S_ADMIN] },
    ],
  },

  /* ------------- Dialers (SADMIN) --------------------------------- */

  {
    label: 'Dialers',
    icon: Headphones,
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'Manage Dialers',
    children: [
      { label: 'Search', to: '/search-dialer', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Dialers.Search Dialer' },
      // Listing — "+ Create Dialer" lives top-right.
      { label: 'All Dialers', to: '/dialer-list', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Dialers.Dialer List' },
      { label: 'Usage', to: '/dialer-usage', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Dialers.Dialer Usage' },
      { label: 'Usage Rollup', to: '/dialer-usage-rollup', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
    ],
  },

  /* ------------- Daily operations (campaigns + builders) ---------- */

  {
    label: 'Campaigns',
    icon: PhoneOutgoing,
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'Manage Campaign',
    children: [
      // Listing — also hosts "+ Compose Campaign" button (→ /add-voice-campaign).
      { label: 'Live', to: '/voice-campaign-summary', roles: [USER] },
      { label: 'History', to: '/historical-voice-campaign-summary', roles: [USER] },
      { label: 'Scheduled', to: '/schedule-voice-campaign-summary', roles: [USER] },
      { label: 'Operations', to: '/campaign-operations', roles: [USER, S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Campaign.Campaign Operations' },
      {
        label: 'Continuous (CvC)',
        to: '/cvc-summary',
        roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
        requiresAnyModuleId: [10],
        permissionPath: 'Manage Campaign.CvC Summary',
      },
      {
        label: 'Continuous (CvC) Logs',
        to: '/cvc-logs',
        roles: [S_ADMIN],
        permissionPath: 'Manage Campaign.CvC Logs',
      },
      { label: 'All Campaigns', to: '/voice-superadmin-campaign-summary', roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Campaign.Voice Campaign Summary' },
      { label: 'Prompt Campaigns', to: '/prompt-voice-superadmin-campaign-summary', roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Campaign.Prompt Voice Campaign Summary' },
      { label: 'History', to: '/historical-campaign-summary-superadmin', roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Campaign.Historical Campaign Summary' },
      { label: 'Allocation', to: '/manage-campaigns-superadmin', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Campaign.Manage Campaigns' },
      { label: 'Voice Base', to: '/view-voice-base', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Campaign.View Voice Base' },
    ],
  },
  {
    label: 'TTS Studio',
    icon: Megaphone,
    roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'TTS Studio',
    inheritedModule: 'TTS Studio',
    children: [
      { label: 'Flows', to: '/tts-flow-list', roles: [USER], requiresAnyModuleId: [7, 10] },
      { label: 'Run Logs', to: '/tts-flow-cont-logs', roles: [USER], requiresAnyModuleId: [10] },
      { label: 'Approval', to: '/tts-flow-approval', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'TTS Studio.Approval' },
      { label: 'Logs', to: '/tts-flow-logs', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'TTS Studio.Logs' },
    ],
  },
  // Add+List pair → collapsed. "+ Add Custom IVR" lives on /ivr-list top-right.
  {
    label: 'IVR Designer',
    icon: PhoneCall,
    to: '/ivr-list',
    roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
    requiresAnyModuleId: [1],
    permissionPath: 'Manage Campaign.Custom IVR List',
  },
  {
    // Agent management — its own module. Used by Inbound Campaigns, but
    // not nested under it.
    label: 'Call Patch Agent',
    icon: Headphones,
    to: '/group-agent-list',
    roles: [USER],
  },

  /* ------------- Content + reporting ------------------------------ */

  {
    label: 'Library',
    icon: BookAudio,
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    children: [
      // Approval queue — first so it's the default landing for approvers.
      // Resellers approve their own direct child users' uploads too, so
      // ADMIN belongs here alongside SADMIN/SUB_SUPER_ADMIN.
      { label: 'Pending IVR Prompts', to: '/all-voice-files', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Voice Library.Prompts for Approval' },
      // Listing — also hosts "+ Add Voice File" button.
      { label: 'IVR Voice', to: '/voice-files', roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Voice Library.All Prompts' },
      {
        label: 'TTS Clips',
        to: '/tts-list',
        roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
        requiresAnyModuleId: [7],
        permissionPath: 'Voice Library.TTS List',
      },
    ],
  },
  {
    label: 'Reports',
    icon: FileText,
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    children: [
      { label: 'Downloads', to: '/download-report', roles: [USER, S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Campaign.Download Report' },
      { label: 'CDR Lookup', to: '/call-detail-records', roles: [USER] },
      {
        label: 'Scheduled Reports',
        to: '/report-scheduling',
        roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
        requiresAnyModuleId: [11, 12],
        permissionPath: 'Manage Campaign.Report Scheduling',
      },
    ],
  },

  /* ------------- Number lists ------------------------------------- */

  {
    label: 'Number Action',
    icon: ListChecks,
    roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
    children: [
      // Listing — Add+View pair collapsed; "+ Add to Blacklist" lives top-right.
      { label: 'Blacklist', to: '/voice-blacklist', roles: [USER, S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Number Action.View Blacklist' },
      { label: 'Bulk Add Blacklist', to: '/bulk-voice-blacklist', roles: [USER] },
      { label: 'Blacklist & DND', to: '/add-blacklist-dnd', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Number Action.Manage Blacklist-DND' },
      { label: 'Bulk Blacklist & DND', to: '/bulk-blacklist-dnd', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Number Action.Manage Bulk Blacklist-DND' },
      // Listing — Add+View pair collapsed; "+ Add Whitelist" lives top-right.
      { label: 'Whitelist', to: '/view-whitelist', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Number Action.View Whitelist' },
      { label: 'Lookup Number', to: '/search-blacklist-dnd', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Number Action.Search DND-Blist-Wlist' },
    ],
  },

  /* ------------- Pricing ------------------------------------------ */

  // Add+View pair → collapsed leaf. "+ Add Voice Plan" lives on /voice-plans top-right.
  {
    label: 'Voice Pricing',
    icon: Wifi,
    to: '/voice-plans',
    roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'Voice Plans.View Voice Plans',
  },
  // Add+View pair → collapsed leaf. "+ Add TTS Plan" lives on /tts-plans top-right.
  {
    label: 'TTS Pricing',
    icon: Wifi,
    to: '/tts-plans',
    roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'TTS Plans.View TTS Plans',
    inheritedModule: 'TTS Plans',
    // Reseller (ADMIN) ko ye menu tabhi dikhe jab uske paas TTS module (id 7) ho.
    // SADMIN / SUB_SUPER_ADMIN ke liye module gating skip hoti hai (modules === null).
    requiresAnyModuleId: [7],
  },

  /* ------------- Team / Admins (role-specific shortcuts) ---------- */

  // Add+List pair → collapsed leaf. Add lives on /sub-user-list top-right.
  {
    label: 'Team',
    icon: Users,
    to: '/sub-user-list',
    roles: [USER],
  },
  // Add+List pair → collapsed leaf. Add lives on /sub-super-admin/user-list top-right.
  {
    label: 'Admins',
    icon: ShieldCheck,
    to: '/sub-super-admin/user-list',
    roles: [S_ADMIN],
  },

  /* ------------- Connectivity ------------------------------------- */

  {
    label: 'CLI Pool',
    icon: PhoneCall,
    roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'Manage CLI',
    requiresAnyModuleId: [5],
    children: [
      { label: 'Allocate', to: '/allocate-cli', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage CLI.Allocate CLI' },
      // Listing — "+ Upload CLI" lives on /cli-list top-right.
      { label: 'All CLIs', to: '/cli-list', roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage CLI.CLI List' },
      { label: 'Pilots', to: '/cli-pilot-list', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage CLI.Pilot no. CLI' },
      { label: 'Archive', to: '/archived-cli', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage CLI.Archived CLI List' },
      { label: 'Dedicated Archive', to: '/archived-dedicated-cli', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'CLI Usage', to: '/cli-usage', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
      {
        label: 'CLI Logs',
        to: '/cli-logs',
        roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
        permissionPath: 'Manage CLI.CLI Logs',
        inheritedPath: 'Manage CLI.CLI Logs',
        requiresAnyModuleId: [5],
      },
    ],
  },
  // USER role — read-only list of own dedicated CLIs (shown only if Dedicated CLI module 5 is assigned).
  {
    label: 'CLI Pool',
    icon: PhoneCall,
    roles: [USER],
    requiresAnyModuleId: [5],
    children: [
      { label: 'All CLIs', to: '/my-dedicated-cli', roles: [USER] },
    ],
  },
  // USER role — single archived-CLI list → leaf.
  // {
  //   label: 'Archive',
  //   icon: PhoneCall,
  //   to: '/user-archive-cli-list',
  //   roles: [USER],
  // },
  {
    label: 'API CLI',
    icon: Globe,
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'Manage API CLI',
    children: [
      // Listing — "+ Upload API CLI" lives top-right.
      { label: 'All API CLIs', to: '/cli-api-list', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage API CLI.Api CLI List' },
      { label: 'Pilots', to: '/cli-api-pilot-list', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage API CLI.Api Pilot no. CLI' },
      { label: 'Archive', to: '/archived-api-cli', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage API CLI.Archived Api CLI List' },
    ],
  },
  {
    label: 'API Keys',
    icon: KeyRound,
    roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
    requiresAnyModuleId: [4],
    children: [
      // Listing — "+ Create API Key" lives top-right.
      { label: 'Keys', to: '/user-api-key-list', roles: [USER] },
      { label: 'Usage', to: '/api-key-summary-list', roles: [USER, S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Api Key.Api Summary' },
      { label: 'History', to: '/api-key-historical-summary-list', roles: [USER, S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Api Key.Api Historical Summary' },
    ],
  },
  {
    label: 'Webhooks',
    icon: Webhook,
    roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
    children: [
      {
        label: 'CDR Webhook',
        to: '/webhook-list',
        roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
        requiresAnyModuleId: [3],
        permissionPath: 'Manage Campaign.Webhook List',
      },
      {
        label: 'SMS',
        to: '/sms-webhook-list',
        roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
        requiresAnyModuleId: [9],
        permissionPath: 'Manage Campaign.SMS Webhook List',
      },
    ],
  },

  /* ------------- Infrastructure (SADMIN) -------------------------- */

  // Add + Bulk Add + Bulk Delete actions live on /group-list top-right.
  {
    label: 'Groups',
    icon: Users,
    to: '/group-list',
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'Manage Groups.Group List',
  },
  // Add + Bulk Add + Bulk Delete actions live on /location-list top-right.
  {
    label: 'Locations',
    icon: MapPin,
    to: '/location-list',
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'Manage Locations',
  },
  // Add+List pair → collapsed leaf. "+ Create Trunk" lives on /trunk-list top-right.
  {
    label: 'Trunks',
    icon: ChartArea,
    to: '/trunk-list',
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
  },

  /* ------------- Admin tools (SADMIN) ----------------------------- */

  {
    label: 'Access Control',
    icon: Cog,
    roles: [S_ADMIN],
    children: [
      { label: 'Modules', to: '/module', roles: [S_ADMIN] },
      { label: 'Roles', to: '/acl-settings', roles: [S_ADMIN] },
    ],
  },
  {
    label: 'Announcements',
    icon: Bell,
    to: '/flash-messages',
    roles: [S_ADMIN],
  },
  {
    label: 'Blocked Words',
    icon: ShieldAlert,
    to: '/blocked-words',
    roles: [S_ADMIN],
  },

  /* ------------- Inbound (placed last per product order) ---------- */

  {
    label: 'Inbound',
    icon: PhoneCall,
    roles: [ADMIN, USER, S_ADMIN, SUB_SUPER_ADMIN],
    // USER + ADMIN need module 6; SADMIN sees Inbound unconditionally.
    requiresAnyModuleId: [6],
    children: [
      // Listing — also hosts "+ New Inbound Campaign" button (→ /add-inbound-campaign).
      { label: 'Live', to: '/inbound-campaign-list', roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'History', to: '/inbound-campaign-historical-list', roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'All Inbound', to: '/inbound-superadmin-campaign-summary', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'Manage Campaign.Inbound Summary' },
    ],
  },
    {
    label: 'API Docs',
    icon: FileText,
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'API Docs',
    inheritedModule: 'API Docs',
    requiresAnyModuleId: [16],
    children: [
      // Sheet Content / Upload Document are management pages (super + sub-super admin).
      // Reseller / user get a dynamic per-sheet / per-document list — see features/api-docs/use-nav-items.ts.
      { label: 'Sheet Content', to: '/superadmin-api-docs/sheet-content', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'API Docs.Sheet Content' },
      { label: 'Upload Document', to: '/superadmin-api-docs/upload-document', roles: [S_ADMIN, SUB_SUPER_ADMIN], permissionPath: 'API Docs.Upload Document' },
    ],
  },

  // Hidden routes still mounted but not surfaced in sidebar:
  //  - /user-profile           (header dropdown)
  //  - /update-user/:id, /update-group/:id, etc.  (action buttons)
  //  - /create-user, /create-group, /add-voice-plan, ...
  //    All "+ Add" pages — reached from listing page top-right buttons,
  //    never directly from the sidebar.
];
